/**
 * CanvasSetupUtilities - Canvas setup and configuration utilities
 *
 * Extracted from EdgeDocsCanvas.V3.tsx for better modularity and testability.
 *
 * This utility provides functions for setting up high-DPI canvases,
 * managing CSS dimensions, and performing common canvas operations.
 */

import { prepareCanvasForHighDPI } from '../../../../utils/Transform';
import { CanvasLogger } from '../../../../../../utils/loggers/CanvasLogger';

/**
 * Parameters for canvas setup
 */
export interface CanvasSetupParams {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  pageWidth: number;
  pageHeight: number;
  scale: number;
  dpr: number;
}

/**
 * Setup canvas for high-DPI rendering and set CSS dimensions
 *
 * This function handles the complete canvas setup process:
 * 1. Calculates CSS dimensions from page dimensions and scale
 * 2. Prepares canvas for high-DPI rendering (sets actual pixel dimensions)
 * 3. Sets CSS dimensions for proper display
 *
 * @param params - Canvas setup parameters
 * @returns Object with CSS dimensions for reference
 */
export function setupHighDpiCanvas(params: CanvasSetupParams): {
  canvasCssWidth: number;
  canvasCssHeight: number;
} {
  const { canvas, ctx, pageWidth, pageHeight, scale, dpr } = params;

  // Calculate CSS dimensions (what the canvas appears as on screen)
  const canvasCssWidth = pageWidth * scale;
  const canvasCssHeight = pageHeight * scale;

  // Prepare canvas for high-DPI rendering (sets actual pixel dimensions)
  prepareCanvasForHighDPI(canvas, ctx, canvasCssWidth, canvasCssHeight, dpr);

  // Set canvas CSS dimensions for proper display
  canvas.style.width = `${canvasCssWidth}px`;
  canvas.style.height = `${canvasCssHeight}px`;

  return {
    canvasCssWidth,
    canvasCssHeight
  };
}

/**
 * Clear canvas area with CSS dimensions
 *
 * Clears the entire canvas using the CSS dimensions (logical pixels).
 * The high-DPI transform ensures this clears the correct physical pixels.
 *
 * @param ctx - Canvas rendering context
 * @param canvasCssWidth - Canvas width in CSS pixels
 * @param canvasCssHeight - Canvas height in CSS pixels
 */
export function clearCanvasArea(
  ctx: CanvasRenderingContext2D,
  canvasCssWidth: number,
  canvasCssHeight: number
): void {
  ctx.clearRect(0, 0, canvasCssWidth, canvasCssHeight);
}

/**
 * Result from canvas setup operation
 */
export interface CanvasSetupResult {
  canvasCssWidth: number;
  canvasCssHeight: number;
  dpr: number;
}

/**
 * Setup canvas for rendering with automatic clearing option
 *
 * Combines canvas setup and optional clearing in one convenient function.
 * This is the main entry point for preparing a canvas for the render cycle.
 *
 * @param params - Canvas setup parameters
 * @param clearCanvas - Whether to clear the canvas after setup (default: true)
 * @param pageIndex - Page index for diagnostic logging (optional)
 * @returns Object with CSS dimensions and DPR for reference
 */
export function setupCanvasForRendering(
  params: CanvasSetupParams,
  clearCanvas: boolean = true,
  pageIndex?: number
): CanvasSetupResult {
  const { ctx, dpr } = params;
  const { canvasCssWidth, canvasCssHeight } = setupHighDpiCanvas(params);

  // 🔬 RENDER_BEGIN diagnostic
  if (pageIndex !== undefined) {
    CanvasLogger.renderCycle.begin(0, pageIndex, 'setupCanvasForRendering', { clearCanvas, clearRect: [0, 0, canvasCssWidth, canvasCssHeight] });
  }

  // Clear canvas if requested
  if (clearCanvas) {
    clearCanvasArea(ctx, canvasCssWidth, canvasCssHeight);
  }

  return {
    canvasCssWidth,
    canvasCssHeight,
    dpr
  };
}
